import React, { useEffect, useState, useMemo } from "react";
import { useTransition, animated } from "@react-spring/web";
import { useDispatch } from "react-redux";
import _ from "lodash";
import cx from "classnames";
import { StreamDetail } from "./types";
import { FileService } from "../../utils/file.service";
import styles from "./streamDetailView.module.scss";
import StreamDetailViewers from "./streamDetailViewers";
import ManageUserAccess from "../../components/managerUserAccess";
import { fetchAssetAnalytics, updateAsset } from "../../redux/actions/assets";
import { updateGroup } from "../../redux/actions/groups";
import Actions from "../../components/actionbar";
import { EditStreamToolbar, EditStreamMenuBar, RenameMenubar } from "./edit";
import { useSelector } from "react-redux";
import {
  addAssetToStream,
  createGroup,
  fetchStreamAnalytics,
  fetchStreamChildren,
  sortStream,
  updateStream,
  invalidateStreamChildren
} from "../../redux/actions/streams";
import ManageSource from "../../components/manageSource";
import { PlaceholderAssetCard } from "../hub/components/assetCard";
import EmptyMessage from "src/components/emptyMessage";
import { Asset, AssetUpload } from "src/types/assets";
import { Group } from "src/types/groups";
import Tooltip from "src/components/tooltip";
import { AddAssetsDialog } from "src/components/asset/add";
import { AddGroupSlider } from "src/components/addGroup";
import {
  getStreamSortPreference,
  getStreamViewerPreference,
  persistStreamViewerPreference,
} from "src/utils/stream.helper";
import { useNavigate } from "react-router-dom";
import { startBackgroundUploadTask } from "src/redux/actions/backgroundTask";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import { STREAM } from "src/constants/adoption/sourceTypes";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import userRoleService from "src/utils/userRoles.service";
import Reveal from "src/components/animation";

interface StreamToolBarProps {
  stream: any;
  show: boolean;
  view: string;
  query: string;
  handleSearchChange: (query: any) => void;
  setOpenUserAccess: (open: boolean) => void;
  setView: (viewType: string) => void;
  setEditAction: (action: string) => void;
  setOpenManageSource: (open: boolean) => void;
  toggle: boolean;
  setToggle: (toggle: boolean) => void;
}

const StreamToolBar: React.FC<StreamToolBarProps> = ({
  stream,
  show,
  view,
  query,
  setOpenUserAccess,
  setView,
  handleSearchChange,
  setEditAction,
  setOpenManageSource,
  toggle,
  setToggle,
}) => {
  const dispatch = useDispatch();
  const {
    trackAddGroupClicked,
    trackAddAssetClicked,
    trackRenameClicked,
    trackManageSourceClicked,
    trackManageUserAccessClicked,
  } = useStreamsAdoption();

  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

  const toggleTranisitions = useTransition(toggle, {
    from: { opacity: 0, transform: "translateY(-5px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-5px)" },
    config: { duration: 200 },
  });

  const seekEnabled = useSelector((state: any) =>
    _.get(state, "accounts.item.seekEnabled", false),
  );

  useEffect(() => {
    api.start();
  }, [show]);

  useEffect(() => {
    return () => {
      dispatch(invalidateStreamChildren(stream.id)); 
    }
  }, [])

  useEffect(() => {
    const viewerType = getStreamViewerPreference();
    if (viewerType) {
      setView(viewerType);
    }
  }, []);

  const handleStreamViewClicked = (viewType: string) => {
    setView(viewType);
    persistStreamViewerPreference(viewType);
  };

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.streamsMenuBar)}>
          <Actions.Container>
            <Actions.Row>
              <Actions.Search query={query} onSearch={handleSearchChange} />
              {seekEnabled && <Actions.Seek />}
              {stream.canEdit && (
                <Actions.IconButton
                  icon="paperflite-manage-hub"
                  tooltip="Edit"
                  onClick={() => setEditAction("editStream")}
                />
              )}
            </Actions.Row>
            <Actions.Row>
              {stream.canEdit && (
                <>
                  <Actions.Dropdown
                    onMouseEnter={() => setToggle(true)}
                    onMouseLeave={() => setToggle(false)}
                  >
                    <Actions.DropdownToggle
                      icon="paperflite-add"
                      onMouseEnter={() => setToggle(true)}
                    />

                    {toggleTranisitions(
                      (style, item) =>
                        item && (
                          <animated.div
                            style={style}
                            className={styles.addEntityDropdown}
                            onMouseEnter={() => setToggle(true)}
                            onMouseLeave={() => setToggle(false)}
                          >
                            <Actions.DropdownMenuItem
                              text="Add asset"
                              onClick={() => {
                                setEditAction("add-asset");
                                trackAddAssetClicked({
                                  streamId: stream.id,
                                  groupId: "",
                                });
                              }}
                            />
                            <Actions.DropdownMenuItem
                              text="Add group"
                              onClick={() => {
                                setEditAction("add-group");
                                trackAddGroupClicked({
                                  streamId: stream.id,
                                  groupId: "",
                                });
                              }}
                            />
                          </animated.div>
                        ),
                    )}
                  </Actions.Dropdown>

                  <Actions.IconButton
                    icon="paperflite-rename"
                    tooltip="Rename"
                    onClick={() => {
                      setEditAction("rename");
                      trackRenameClicked({
                        streamId: stream.id,
                        sourceCta: STREAM,
                      });
                    }}
                  />
                  <Actions.IconButton
                    icon="paperflite-user-settings-property"
                    tooltip="User Access"
                    onClick={() => {
                      setOpenUserAccess(true);
                      trackManageUserAccessClicked({ streamId: stream.id });
                    }}
                  />
                  {userRoleService.canAccessDataSource() && (
                    <Actions.IconButton
                      icon="paperflite-sync"
                      tooltip="Manage Content Source"
                      onClick={() => {
                        setOpenManageSource(true);
                        trackManageSourceClicked({ streamId: stream.id });
                      }}
                    />
                  )}
                </>
              )}
              <Actions.Set>
                <Actions.SetItem
                  className={cx({ [styles.primaryAction]: view === "card" })}
                  icon="paperflite-categories"
                  tooltip="Card"
                  onClick={() => handleStreamViewClicked("card")}
                />
                <Actions.SetItem
                  className={cx({ [styles.primaryAction]: view === "list" })}
                  icon="paperflite-list"
                  tooltip="List"
                  onClick={() => handleStreamViewClicked("list")}
                />
                <Actions.SetItem
                  className={cx({ [styles.primaryAction]: view === "grid" })}
                  icon="paperflite-column-view"
                  tooltip="Grid"
                  onClick={() => handleStreamViewClicked("grid")}
                />
              </Actions.Set>
            </Actions.Row>
          </Actions.Container>
        </animated.div>
      ),
  );
};

const StreamDetailView: React.FC<{ id: string; isExpanded: boolean }> = ({
  id,
  isExpanded,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    trackAddGroupConfirmClicked,
    trackRenameConfirmClicked,
    trackGroupAdded,
    trackAssetRenamed,
    trackGroupRenamed,
    trackStreamEdited,
  } = useStreamsAdoption();

  const loadingStream = useSelector((state: any) =>
    _.get(state, `stream.items.[${id}].loading`),
  );
  const stream = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].item`, null),
  );
  const loadingStreamChildren = useSelector(
    (state: any) =>
      _.get(state, `stream.items[${id}].loadingChildren`) || false,
  );
  const streamChildren = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].item.children`, null),
  );
  const fetchedChildren = useSelector(
    (state: any) =>
      _.get(state, `stream.items[${id}].fetchedChildren`) || false,
  );
  const createdGroup = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].groupCreation.created`, null),
  );
  const creatingGroup = useSelector(
    (state: any) =>
      _.get(state, `stream.items[${id}].groupCreation.creating`) || false,
  );
  const analytics = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].item.analytics`, null),
  );
  const groupCreated = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].groupCreation.item`, []),
  );
  const groupUpdating = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].updatingGroup`, false),
  );
  const updating = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].updation.updating`, false),
  );
  const updated = useSelector((state: any) =>
    _.get(state, `stream.items[${id}].updation.updated`, false),
  );
  const { isEntityMoving, isEntityMoved } = useSelector((state: any) => {
    return {
      isEntityMoving: _.get(state, `entities.move.moving`),
      isEntityMoved: _.get(state, `entities.move.moved`),
    };
  });
  const [view, setView] = useState("grid");
  const [groups, setGroups] = useState<Group>();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [openUserAccess, setOpenUserAccess] = useState(false);
  const [entities, setEntities] = useState<(Asset | Group)[]>([]);
  const [editAction, setEditAction] = useState("");
  const [openManageSource, setOpenManageSource] = useState(false);
  const [invalidateSelectedEntities, setInvalidateSelectedEntities] =
    useState(false);
  const [query, setQuery] = useState("");
  const [name, setName] = useState("");
  const [toggle, setToggle] = useState(false);
  const [error, setError] = useState("");

  const sortPreference = getStreamSortPreference();

  const createGroupProps: IUseFetchProps = {
    action: createGroup,
    loading: creatingGroup,
    succeeded: createdGroup,
    response: groupCreated,
    error: null,
  };

  const { handleFetch: createGroupRequest } = useFetch(createGroupProps);

  const updateStreamProps: IUseFetchProps = {
    action: updateStream,
    loading: updating,
    succeeded: updated,
    response: stream,
    error: null,
  };

  const { handleFetch: updateStreamRequest } = useFetch(updateStreamProps);

  useEffect(() => {
    if (!loadingStreamChildren) {
      const sortPreference = getStreamSortPreference();
      dispatch(
        fetchStreamChildren({
          id: id,
          page: 0,
          limit: 25,
          sort: { order: sortPreference.orderBy, type: sortPreference.sortBy },
        }),
      );
      dispatch(fetchStreamAnalytics({ id: id }));
    }
  }, []);

  useEffect(() => {
    if (stream) {
      setName(stream.name);
    }
  }, [stream]);

  useEffect(() => {
    if (_.size(streamChildren) > 0) {
      getEntities();
    }
  }, [fetchedChildren]);

  useEffect(() => {
    if (_.size(streamChildren) > 0) {
      getEntities();
      // dispatch(fetchStreamAnalytics({ id: id }));
      //commenting this out as it is causing re-renders and it's need to be dispatched only once
    }
  }, [streamChildren]);

  useEffect(() => {
    assets.forEach((asset) => {
      if (!asset.loading) {
        dispatch(
          fetchAssetAnalytics({ streamId: stream.id, assetId: asset.id}),
        );
      }
    });
  }, [assets]);

  useEffect(() => {
    if (createdGroup) {
      handleClose();
    }
  }, [createdGroup]);

  useEffect(() => {
    if (!updating && !groupUpdating) {
      setEditAction("");
      setEntities([]);
      dispatch(
        sortStream({
          id: id,
          sort: { order: sortPreference.orderBy, type: sortPreference.sortBy },
        }),
      );
    }
  }, [updating, groupUpdating]);

  useEffect(() => {
    if (!isEntityMoving && isEntityMoved) {
      dispatch(
        fetchStreamChildren({
          id: id,
          page: 0,
          limit: 25,
          sort: { order: sortPreference.orderBy, type: sortPreference.sortBy },
        }),
      );
    }
  }, [isEntityMoving, isEntityMoved]);

  const getEntities = () => {
    let assets = stream.children.filter(
      (child: { entityType: string }) => child.entityType === "asset",
    );
    assets = assets.map((asset: any) => ({
      ...asset,
      type: FileService.getFileType(_.get(asset, "metadata")) || "",
    }));
    setAssets(assets);

    let groups = stream.children.filter(
      (child: { entityType: string }) => child.entityType === "group",
    );
    setGroups(groups);
  };

  const handleClose = () => {
    setOpenUserAccess(false);
    setOpenManageSource(false);
    setEditAction("");
    setError("");
    setToggle(false);
  };

  const handleUpdateEntities = (entities: (Asset | Group)[]) => {
    setEntities(entities);
  };

  const handleRename = () => {
    const assetIds: string[] = [];
    const groupIds: string[] = [];

    if (_.size(name) > 0 && _.size(name) <= 200 && stream.name !== name) {
      const request = {
        ...stream,
        id: stream.id,
        name: name,
        banner: {},
      };

      updateStreamRequest({ request }).then((response) => {
        trackStreamEdited({ stream: response });
      });
      setError("");
    }

    entities.forEach((entity: Asset | Group) => {
      if (entity.entityType === "asset") {
        dispatch(
          updateAsset({
            id: entity.id,
            request: entity,
            streamId: stream.id,
            type: "rename",
            event: trackAssetRenamed,
          }),
        );
        assetIds.push(entity.id);
      } else if (entity.entityType === "group") {
        const request = {
          entity: entity,
          streamId: stream.id,
          parent: "",
          type: "rename",
          event: trackGroupRenamed,
        };
        dispatch(updateGroup(request));
        groupIds.push(entity.id);
      }
    });

    trackRenameConfirmClicked({
      streamId: id,
      groupIds: groupIds,
      assetIds: assetIds,
    });
  };

  const handleSearchChange = (query: any) => {
    if (!query || query === "") {
      return;
    }
    navigate(`/streams/${id}/search?query=${query}`);
  };

  const handleSelectEntity = (entity: Asset | Group) => {
    setInvalidateSelectedEntities(false);
    if (entities.some((item) => item.id === entity.id)) {
      setEntities(entities.filter((item) => item.id !== entity.id));
    } else {
      setEntities([...entities, entity]);
    }
  };

  const handleCloseAddAsset = () => {
    setEditAction("");
  };

  const handleCancelAction = () => {
    setEditAction("");
    setEntities([]);
    setInvalidateSelectedEntities(true);
  };

  const handleAddAssetFinish = (entities: any[]) => {
    if (_.size(entities) > 0) {
      const uploading = entities.filter(
        (item) => item.entityType == "asset-upload",
      );
      if (!_.isEmpty(uploading)) {
        const assest = entities.filter((item) =>
          _.includes(["asset", "asset-upload"], item.entityType),
        );
        dispatch(
          startBackgroundUploadTask({
            assets: assest as [Asset | AssetUpload],
            entity: {
              id: stream.id,
              entityType: "stream",
              name: stream.name,
              url: `/streams/${stream.id}`,
            },
          }),
        );
      } else {
        const assets = entities.filter((item) => item.entityType == "asset");
        dispatch(
          addAssetToStream({
            streamId: stream.id,
            assets: assets.map((item) => item.id),
          }),
        );
      }
    }

    handleCloseAddAsset();
  };

  const handleCreateGroup = (groupName: string) => {
    createGroupRequest({ groupName: groupName, streamId: stream.id }).then(
      (response) => {
        trackGroupAdded({ groupName: response.name, streamId: stream.id });
      },
    );
    trackAddGroupConfirmClicked({
      streamId: id,
      group_name: groupName,
      sourceGroupId: "",
    });
  };

  const renderErrors = () => {
    return (
      <Reveal show={!!error}>
        <div className={styles.error}>
          <i className="paperflite-danger"></i>
          {error}
        </div>
      </Reveal>
    );
  };

  const handleNameChange = (name: string) =>{
    const newName = name;
    if (editAction === "rename") {
      setName(newName);
      if (newName.length === 0) {
        setError("Please enter a valid stream name");
      } else if (newName.length > 200) {
        setError("Stream name cannot be greater than 200 characters");
      } else {
        setError("");
      }
    }
  }

  const assetsCount = _.get(analytics, "item.assetCount", 0);
  const layoutId = localStorage.getItem("hubLayout");

  return (
    <div
      className={cx(styles.container, {
        [styles.expanded]: true,
      })}
    >
      {loadingStream && (
        <div
          className={cx(styles.entity, styles.placeholder)}
          style={{ width: "100%" }}
        >
          <div className={cx(styles.placeholderName, styles.block)}></div>
          <div className={cx(styles.placeholderCount, styles.block)}></div>
        </div>
      )}

      {!loadingStream && stream && (
        <div className={styles.header}>
          <div className={styles.details}>
            <span className={styles.titleContainter}>
              <i
                className={cx("paperflite-left-arrow", styles.backIcon, {
                  [styles.hideIcon]: editAction === "rename",
                  [styles.moveIcon]:
                    editAction !== "" && editAction !== "rename",
                })}
                onClick={() => navigate(`/hub/layout/${layoutId}`)}
              ></i>
              <div
                className={cx(styles.title, {
                  [styles.moveTitle]: editAction !== "",
                  [styles.renameStream]: editAction === "rename",
                })}
              >
                <Tooltip content={stream.name} placement="bottom">
                  {editAction === "rename" ? (
                    <>
                      <input
                      disabled={updating}
                      className={cx({ [styles.updating]: updating })}
                      type="text"
                      placeholder="Provide a stream name"
                      value={name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      />
                      {error && renderErrors()}
                    </>
                  ) : (
                    <span>{stream.name}</span>
                  )}
                </Tooltip>
              </div>
            </span>
            <span className={styles.assetsCount}>
              {assetsCount} {assetsCount > 1 ? "Assets" : "Asset"}
            </span>
          </div>

          {
            <RenameMenubar
              show={editAction === "rename"}
              onConfirm={handleRename}
              updating={updating || groupUpdating}
              onCancel={() => {
                setEditAction("");
                setError("");
                setName(stream.name);
              }}
            />
          }

          {
            <EditStreamMenuBar
              show={editAction === "editStream"}
              onCancel={handleCancelAction}
            />
          }

          <EditStreamToolbar
            id={id}
            show={editAction == "editStream"}
            onFinish={handleCancelAction}
            entities={entities}
          />

          {
            <StreamToolBar
              stream={stream}
              show={editAction === ""}
              setOpenUserAccess={setOpenUserAccess}
              setOpenManageSource={setOpenManageSource}
              view={view}
              setView={setView}
              query={query}
              handleSearchChange={handleSearchChange}
              setEditAction={setEditAction}
              toggle={toggle}
              setToggle={setToggle}
            />
          }

          {editAction === "add-asset" && (
            <AddAssetsDialog
              show={editAction == "add-asset"}
              selectableEntityTypes={["asset"]}
              source={{
                type: "stream",
                entity: stream,
              }}
              onFinish={handleAddAssetFinish}
              onClose={handleCloseAddAsset}
            />
          )}

          {editAction === "add-group" && (
            <AddGroupSlider
              creating={creatingGroup}
              handleClose={handleClose}
              handleCreateGroup={handleCreateGroup}
            />
          )}
        </div>
      )}

      {((loadingStreamChildren && !fetchedChildren) || loadingStream) && (
        <div className={styles.loadingChildren}>
          {_.times(4, (i) => (
            <PlaceholderAssetCard key={i} />
          ))}
        </div>
      )}

      {!loadingStream &&
        fetchedChildren &&
        !loadingStreamChildren &&
        _.size(streamChildren) === 0 && (
          <div className={styles.emptyContainer}>
            <EmptyMessage
              width={600}
              image={"/images/streams/empty.svg"}
              heading="Nothing to see here, let’s make this stream interesting..."
              message=""
              action={{
                title: "Add asset",
                icon: "paperflite-add",
                onClick: () => {
                  setEditAction("add-asset");
                },
              }}
            />
          </div>
        )}

      {(_.size(assets) > 0 || _.size(groups) > 0) && (
        <div className={styles.details}>
          <StreamDetailViewers
            entityType="stream"
            updating={updating}
            viewer={view}
            groups={groups}
            assets={assets}
            editAction={editAction}
            handleUpdateEntities={handleUpdateEntities}
            handleSelectEntity={handleSelectEntity}
            invalidateSelectedEntities={invalidateSelectedEntities}
          />
        </div>
      )}

      {openUserAccess && (
        <ManageUserAccess
          openUserAccess={openUserAccess}
          stream={stream}
          handleClose={handleClose}
        />
      )}

      {openManageSource && (
        <ManageSource
          openManageSource={openManageSource}
          stream={stream}
          handleClose={handleClose}
          entityType="stream"
        />
      )}
    </div>
  );
};

export default StreamDetailView;
